import React, { useState } from 'react';

import { Popover } from 'antd';

import styled from 'styled-components';

import { showEventTime } from '@libs/EventDetails';
import { isBookingEvent, isJobEvent } from '@libs/EventTypes';

import { useUserGuiConfig } from '@contexts/UserGuiConfigContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import useEventStyle from '@hooks/CachedLists/UseEventStyle';

import AuthorAndDepartmentForEventCard from '@components/CreatedBy/AuthorAndDepartmentForEventCard';
import NwIcon from '@components/Gui/NwIcon';

import EventAddress from './EventAddress';
import EventCardDivider from './EventCardDivider';
import EventCardTooltip from './EventCardTooltip';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const EventContentMultiDayContainer = styled.div`
    display: flex;
`;

const EventContentInner = styled.div`
    color: ${(props) => props.color && props.color};
    height: 100%;

    &.event-lapse {
        width: ${(props) => `calc(100% / ${props.multiDayDuration})`};
    }

    .event-content {
        padding: 0.2rem 0.5rem 0.25rem 0.5rem;
        min-height: ${(props) => !props.singleLine && !props.agendaView && '4rem'};

        .event-content-top {
            padding: 0 0.25rem 0 0;
            display: flex;
            width: 100%;
            justify-content: space-between;
            align-items: flex-start;

            div {
                font-size: 0.7rem;
                line-height: 1.2em;
                padding-bottom: 2px;
                font-family: 'Chivo Mono', monospace;

                span {
                    display: block;
                }
            }
        }

        .event-call-time {
            font-size: 0.7rem;
            line-height: 1.2em;
            padding-bottom: 2px;
            font-family: 'Chivo Mono', monospace;
            text-transform: uppercase;
        }
        .event-title {
            line-height: 1.15em;
            font-size: 0.9rem;
            font-weight: 600;
            text-transform: uppercase;
            overflow: hidden;
            text-overflow: ellipsis;

            svg {
                margin-right: 0.5em;
                font-size: 0.85rem;
            }
        }

        .event-subtitle {
            padding-top: 0.25rem;
            padding-bottom: 0.25rem;
            font-size: 0.75rem;
            line-height: 1.15em;
            overflow: hidden;
            text-overflow: ellipsis;

            em {
                font-style: normal;
                text-transform: uppercase;
            }
        }

        .event-subtitle-spacer {
            height: 0.3rem;
        }

        .event-card-author {
            margin-top: 0.5rem;
            font-size: 0.6rem;
            opacity: 0.85;
            text-transform: uppercase;
            //font-style: italic;
            padding: 0.15rem 0 0.25rem 0;
            height: 0.9rem;
            overflow: hidden;
        }
    }

    .event-invisible {
        opacity: 0 !important;
    }

    .event-models {
        margin-top: 4px;
    }

    .cancel {
        ${(props) => props.theme.LineThrough(1)}
    }

    .event-address {
        font-size: ${(props) => props.theme.FontSizeSm};
        p {
            margin: 0;
        }
    }
`;

const EventCard = React.memo(
    ({ event, model, agendaView, monthView }) => {
        const {
            Acknowledgment,
            Address,
            allDay,
            Cancel,
            Description,
            eventType,
            FromDate,
            grouped,
            internalDates,
            ModelsDates,
            modelsNumber,
            multiDayDuration,
            OptionNumber,
            Project,
            title,
            ToDate,
            TypeName,
        } = event;

        const [showPopover, setShowPopover] = useState(false);

        const eventstyle = useEventStyle(event, event.TypeName);
        const isBooking = isBookingEvent(TypeName);

        const userGuiConfig = useUserGuiConfig();
        const mixedEventsChart = userGuiConfig && userGuiConfig.schedule && userGuiConfig.schedule.mixedEventsChart;

        const handleMouseDown = () => {
            document.body.classList.add('dragging');
        };

        const handleMouseUp = () => {
            document.body.classList.remove('dragging');
        };

        const getClassName = (first, last) => {
            const classes = [];
            classes.push('event');
            classes.push('event-with-tooltip');
            if (first) {
                classes.push('event-first-day');
            }
            if (last) {
                classes.push('event-last-day');
            }
            return classes.join(' ');
        };

        const getModelOptionNumber = () => {
            if (model) {
                if (isJobEvent(TypeName)) {
                    if (OptionNumber && OptionNumber > 0 && OptionNumber < 10) {
                        return ` [${OptionNumber}]`;
                    }
                }
            }
            return '';
        };

        const getEventContent = (details) => {
            const modnum = details ? details.modelsNumber : modelsNumber;
            const evtitle = details ? details.Name : title ? title : eventstyle.name;
            const cancel = details ? details.Cancel : Cancel;

            let timetoshow = '';
            const evdetails = details || event;
            if (evdetails.ModelsDates && evdetails.ModelsDates.length) {
                if (evdetails.ModelsDates.length > 1) {
                    timetoshow = evdetails.ModelsDates.length + ' time frames';
                } else {
                    timetoshow = showEventTime(
                        evdetails.ModelsDates[0].FromDate,
                        evdetails.ModelsDates[0].ToDate,
                        evdetails.ModelsDates[0].StartBreak,
                        evdetails.ModelsDates[0].EndBreak
                    );
                }
            } else {
                timetoshow = showEventTime(
                    evdetails.FromDate,
                    evdetails.ToDate,
                    evdetails.StartBreak,
                    evdetails.EndBreak
                );
            }

            return (
                <Popover
                    content={
                        <EventCardTooltip
                            event={event}
                            eventstyle={eventstyle}
                            model={model}
                            TypeName={TypeName}
                            FromDate={details ? details.FromDate : FromDate}
                            ToDate={details ? details.ToDate : ToDate}
                            title={details ? details.Name : title}
                            Description={Description}
                            Project={Project}
                            ModelsDates={details ? details.ModelsDates : ModelsDates}
                            grouped={grouped}
                            Address={Address}
                        />
                    }
                    trigger="hover"
                    mouseEnterDelay={0.25}
                    placement="rightTop"
                    overlayClassName="event-card-popover"
                    onOpenChange={setShowPopover}
                >
                    <div className="event-content">
                        {(!agendaView || (agendaView && !allDay)) && (
                            <div className={`event-content-top ${agendaView && cancel ? 'cancel' : ''}`}>
                                {TypeName !== 'Accommodation' && <div>{timetoshow}</div>}
                                {!agendaView &&
                                    TypeName !== 'Accommodation' &&
                                    TypeName !== 'Notice' &&
                                    (modnum > 0 ? (
                                        <div>{`${modnum} model${modnum > 1 ? 's' : ''}`}</div>
                                    ) : (
                                        <div>no models</div>
                                    ))}
                            </div>
                        )}
                        {allDay && agendaView && (
                            <div className="event-content-top">
                                {/* <div>ALL DAY</div> */}
                                <div>{timetoshow}</div>
                            </div>
                        )}
                        {evdetails.CallTime && <div className="event-call-time">Call Time: {evdetails.CallTime}</div>}
                        <div className={`event-title ${cancel ? 'cancel' : ''}`}>
                            {isJobEvent(TypeName) && event.Locked && !event.Invoiced && (
                                <NwIcon icon={byPrefixAndName.fass['dollar-sign']} />
                            )}
                            {isJobEvent(TypeName) && event.Invoiced && (
                                <NwIcon icon={byPrefixAndName.fass['file-invoice']} />
                            )}
                            {Acknowledgment === 'Checked' && <NwIcon icon={byPrefixAndName.fass['check']} />}
                            {eventstyle.icon && <NwIcon icon={eventstyle.icon} />}
                            {TypeName === 'Accommodation' && <>{`${getContactName(event.ModelID)} @ `}</>}
                            {evtitle}
                            {getModelOptionNumber()}
                        </div>
                        {Project ? (
                            <div className={`event-subtitle ${cancel ? 'cancel' : ''}`}>
                                {Project.Name.toLowerCase() !== evtitle.toLowerCase() ? (
                                    <>
                                        <em>{Project.Name}</em>
                                        {Project.Product && ` / ${Project.Product}`}
                                    </>
                                ) : (
                                    Project.Product && <>{Project.Product}</>
                                )}
                            </div>
                        ) : isBooking ? (
                            <div className="event-subtitle">&nbsp;</div>
                        ) : (
                            <div className="event-subtitle-spacer"></div>
                        )}
                        {!monthView &&
                            model &&
                            isBooking &&
                            Address &&
                            (Address.Name || Address.Street || Address.City || Address.Country) && (
                                <>
                                    <EventCardDivider color={eventstyle.color}>Address</EventCardDivider>
                                    <EventAddress address={Address} inline showAccess={false} />
                                </>
                            )}
                        {model && event && (isBooking || mixedEventsChart) && (
                            <div className="event-card-author">
                                <AuthorAndDepartmentForEventCard object={event} />
                            </div>
                        )}
                    </div>
                </Popover>
            );
        };

        const isSingleLine = () => TypeName === 'Accommodation';

        let colorformultidayjob = null;

        const MultiDayEventContentInner = ({ date, index }) => {
            const dateevent = { ...date.details };
            dateevent.TypeName = event.TypeName;
            dateevent.SubTypeCode = event.SubTypeCode;
            const datestyle = useEventStyle(dateevent, event.TypeName);
            const getStyleForMultiDay = (empty) => {
                const colors = {};
                colors.color = datestyle && datestyle.color;
                if (empty) {
                    if (isJobEvent(event.TypeName)) {
                        colors.style = {
                            width: `${100 / multiDayDuration}%`,
                            background: `repeating-linear-gradient(
                            -55deg,
                            ${colorformultidayjob},
                            ${colorformultidayjob} 1px,
                            #fff 2px,
                            #fff 5px
                        )`,
                        };
                    } else {
                        colors.style = {
                            width: `${100 / multiDayDuration}%`,
                            background: `repeating-linear-gradient(
                            -55deg,
                            ${datestyle.backgroundColor},
                            ${datestyle.backgroundColor} 1px,
                            #fff 2px,
                            #fff 5px
                        )`,
                        };
                    }
                } else {
                    colors.style = { width: `${100 / multiDayDuration}%`, background: datestyle.backgroundColor };
                    if (isJobEvent(event.TypeName) && date.first) {
                        colorformultidayjob = datestyle.backgroundColor;
                    }
                }
                return colors;
            };

            if (date.empty) {
                return (
                    <EventContentInner
                        key={index}
                        singleLine={isSingleLine()}
                        agendaView={agendaView}
                        {...getStyleForMultiDay(true)}
                        className="event event-lapse"
                        multiDayDuration={multiDayDuration}
                    >
                        <div className="event-content">&nbsp;</div>
                    </EventContentInner>
                );
            } else {
                return (
                    <EventContentInner
                        key={index}
                        singleLine={isSingleLine()}
                        agendaView={agendaView}
                        {...getStyleForMultiDay(false)}
                        className={getClassName(date.first, date.last)}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                    >
                        {getEventContent(date.details)}
                    </EventContentInner>
                );
            }
        };

        switch (eventType) {
            case 'empty':
                return (
                    <EventContentInner singleLine={isSingleLine()} className="event event-invisible">
                        <div className="event-title"></div>
                    </EventContentInner>
                );
            case 'no-job':
                return (
                    <EventContentInner singleLine={isSingleLine()} className="event">
                        <div className="event-title"></div>
                    </EventContentInner>
                );
            case 'multi-day':
                return (
                    <EventContentMultiDayContainer className="multi-day-container">
                        {internalDates.map((date, index) => (
                            <MultiDayEventContentInner date={date} index={index} key={index} />
                        ))}
                    </EventContentMultiDayContainer>
                );
            case 'long-day':
                return (
                    <EventContentInner
                        singleLine={isSingleLine()}
                        agendaView={agendaView}
                        color={eventstyle && eventstyle.color}
                        style={{ background: eventstyle.backgroundColor }}
                        className={getClassName(true, true)}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                    >
                        {getEventContent()}
                    </EventContentInner>
                );
            case 'single-day':
                return (
                    <EventContentInner
                        singleLine={isSingleLine()}
                        agendaView={agendaView}
                        color={eventstyle && eventstyle.color}
                        className={getClassName(true, true)}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        style={{ background: eventstyle.backgroundColor }}
                    >
                        {getEventContent()}
                    </EventContentInner>
                );
            default:
                return (
                    <EventContentInner singleLine={isSingleLine()} className="event" agendaView={agendaView}>
                        {getEventContent()}
                    </EventContentInner>
                );
        }
    },
    (prevProps, nextProps) => {
        // Memoization selettiva: solo se le props essenziali cambiano
        return (
            prevProps.event.ID === nextProps.event.ID &&
            prevProps.event.title === nextProps.event.title &&
            prevProps.event.Description === nextProps.event.Description &&
            prevProps.event.FromDate === nextProps.event.FromDate &&
            prevProps.event.ToDate === nextProps.event.ToDate &&
            prevProps.model === nextProps.model &&
            prevProps.agendaView === nextProps.agendaView &&
            prevProps.monthView === nextProps.monthView
        );
    }
);

export default EventCard;
